// Portfolio content — Tharak Koneni
export const portfolioData = {
  personal: {
    name: "Tharak Koneni",
    title: "Developer",
    email: "tharak.env@gmail.com",
    phone: "(813) 534-2315",
    github: "https://github.com/tah-rak",
    linkedin: "https://www.linkedin.com/in/tharak-k",
    photo: "images/profile.jpg",
    tagline: "I build production ML systems and the research tooling around them.",
    intro: "Currently a graduate researcher at the University of South Florida, working on language models, healthcare data, and the messy parts in between.",
    resume: "https://drive.google.com/file/d/1Coj9Eaclub3kCnhb7x1bawN1OF92mz5R/view?usp=drive_link"
  },

  about: {
    paragraphs: [
      "I'm a developer who likes working at the intersection of research and real software. Most days that means turning a rough idea or paper into something you can actually run, measure, and trust.",
      "I spend a lot of time around language models, data pipelines, and the small details that decide whether a system holds up in production. The work is half code, half careful reading, half talking to the people who will end up using it. (Yes, that's three halves. Things rarely add up cleanly.)",
      "Right now I'm pursuing my Master's in Computer Science at the University of South Florida, where I help run experiments on LLM fine-tuning, evaluation, and safety. Before that I worked across healthcare research labs and an industry data team, shipping pipelines, microservices, and models that other people relied on.",
      "Outside of the work, I like leading small teams, writing things down, and the kind of side projects that start at midnight and never quite finish."
    ]
  },

  skills: {
    languages: ["Python", "Java", "C/C++", "JavaScript"],
    frameworks: ["PyTorch", "TensorFlow", "React", "Spring Boot", "Node.js", "Flask"],
    cloud: ["AWS", "Azure", "Terraform", "Firebase", "Snowflake", "Databricks"],
    databases: ["PostgreSQL", "MongoDB", "MySQL", "Cassandra", "DynamoDB", "Hadoop"],
    tools: ["Docker", "Git", "Jenkins", "Airflow", "JIRA", "Postman"]
  },

  expertise: [
    "Machine Learning",
    "Natural Language Processing",
    "MLOps & Pipelines",
    "Research Engineering",
    "Data Engineering",
    "Predictive Modeling",
    "LLM Evaluation",
    "Statistical Analysis"
  ],

  education: [
    {
      id: 1,
      institution: "University of South Florida",
      degree: "M.S. in Computer Science",
      duration: "Aug 2024 → May 2026",
      gpa: "3.61 / 4.0",
      coursework: ["Machine Learning", "AI", "NLP", "Data Structures", "Operating Systems", "Computer Architecture"]
    },
    {
      id: 2,
      institution: "New Horizon College of Engineering",
      degree: "B.E. in Computer Science",
      duration: "Jul 2020 → Jun 2024",
      gpa: "8.46 / 10",
      coursework: ["Computer Networks", "DBMS", "Pattern Recognition", "Big Data Analytics", "Compiler Design", "OOP"]
    }
  ],

  publications: [
    {
      id: 1,
      title: "Smart Predictive Enhancement for Safeguarding Well-being Using Machine Learning",
      venue: "IEEE ICRISST 2024",
      description: "Predictive models for mental health and well-being, combining classical ML with feature engineering on noisy survey data.",
      link: "https://ieeexplore.ieee.org/document/10921958",
      tags: ["Machine Learning", "Healthcare", "Predictive Modeling"]
    },
    {
      id: 2,
      title: "NCS in Security, Based on Self Organizing Maps",
      venue: "IJSRCSEIT 2023, Vol. 9, Issue 8",
      description: "Neural network approaches to network security using self-organizing maps for anomaly detection.",
      link: "https://ijsrcseit.com/home/issue/view/article.php?id=CSEIT239832",
      tags: ["Neural Networks", "Security", "SOM"]
    }
  ],

  experience: [
    {
      id: 1,
      company: "Restaurant Brands International",
      position: "Software Engineer (AI)",
      duration: "Jan 2026 → Present",
      summary: "Building backend services and event-driven AWS systems that power restaurant catalog, inventory, and pricing operations at scale.",
      points: [
        "Build backend services in Python and Go that support restaurant catalog, inventory, and pricing operations at scale.",
        "Develop event-driven AWS workflows with Lambda, SQS, and EventBridge to process updates reliably and recover failed jobs automatically.",
        "Create Amazon Bedrock workflows for product data validation, attribute enrichment, and automated review across millions of records.",
        "Improve system performance and reliability through PostgreSQL tuning, CloudWatch monitoring, structured logging, and automated validation."
      ]
    },
    {
      id: 2,
      company: "USF School of Information",
      position: "Graduate Assistant",
      duration: "Aug 2025 → May 2026",
      summary: "Built FastAPI applications and scalable serverless APIs over university documents and academic data, backed by automated evaluation pipelines.",
      points: [
        "Built Python and FastAPI applications that retrieve information from university documents and structured academic data.",
        "Developed scalable APIs using API Gateway, Lambda, S3, and DynamoDB to support applications across multiple academic domains.",
        "Created automated evaluation pipelines using RAGAS, CloudWatch, and OpenTelemetry to identify retrieval, API, and model issues before release.",
        "Helped standardize testing, AWS deployment, system design, and Git-based development practices across the research group."
      ]
    },
    {
      id: 3,
      company: "Restaurant Brands International",
      position: "Software Engineer (AI) Intern",
      duration: "May 2025 → Aug 2025",
      summary: "Developed backend services and asynchronous AWS data workflows for transaction and customer processing, deployed on Amazon ECS.",
      points: [
        "Developed Python and Go backend services for transaction and customer workflows, including validation, duplicate detection, and request processing.",
        "Built TypeScript integrations and asynchronous AWS workflows to ingest data and trigger processing jobs across S3, Lambda, and Snowflake.",
        "Deployed Dockerized data-processing and inference services on Amazon ECS with logging, health checks, retries, and failure recovery.",
        "Automated testing, Terraform infrastructure, and CI/CD workflows to make application releases faster and more consistent."
      ]
    },
    {
      id: 4,
      company: "USF College of Public Health & College of Medicine",
      position: "Software Engineer",
      duration: "Aug 2024 → May 2025",
      summary: "Built Java APIs and event-driven pipelines for high-volume public health and medical data applications.",
      points: [
        "Developed Java APIs and backend pipelines for retrieving, cleaning, and processing data used across public health and medical applications.",
        "Improved PostgreSQL performance through indexing and query optimization while supporting high-volume application workloads.",
        "Built event-driven workflows with AWS Lambda and SQS to validate requests and coordinate asynchronous processing between applications.",
        "Improved application reliability with structured logging, health checks, CloudWatch monitoring, and automated failure alerts."
      ]
    }
  ],

  achievements: [
    {
      id: 1,
      title: "Editorial Head, College Magazine",
      description: "Led a 10-member team across ideation, content, and the final publication of the college magazine.",
      organization: "New Horizon College of Engineering"
    },
    {
      id: 2,
      title: "Winner, NHCE Quantum Hackathon",
      description: "Built a platform of pre-designed resume templates to help students apply faster.",
      organization: "New Horizon College of Engineering"
    },
    {
      id: 3,
      title: "Winner, NHCE Idea Hackathon",
      description: "Designed a system to improve the security and safety of payments in public transport.",
      organization: "New Horizon College of Engineering"
    }
  ]
};
